import {useRefreshMe} from '../../hooks/session/useRefreshMe';
import {useLogoutMutation} from '../../hooks/session/mutations/useLogoutMutation';
import {useRemoveAccountMutation} from '../../hooks/session/mutations/useRemoveAccountMutation';
import {useUpdateProfileMutation} from '../../hooks/session/mutations/useUpdateProfileMutation';
import {useUpdateAppearanceModeMutation} from '../../hooks/session/mutations/useUpdateAppearanceModeMutation';
import {useUnlinkLinkedinMutation} from '../../hooks/session/mutations/useUnlinkLinkedinMutation';
import CheckIcon from '@mui/icons-material/Check';
import LinkedinIcon from '@mui/icons-material/LinkedIn';
import {
  Alert,
  Card,
  Divider,
  TextField,
  CircularProgress,
  Switch,
  Table,
  TableRow,
  TableBody,
  Box,
  Typography,
  Avatar,
  Button,
  CardContent,
  CardActions,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {EmailSettings} from '@perfectpost/perfect-post-common';
import * as Sentry from '@sentry/react';
import {AuthError, updatePassword} from 'aws-amplify/auth';
import {ConsoleLogger} from 'aws-amplify/utils';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {useMixpanelInstance} from '../../analytics/useMixpanelInstance';
import {PasswordChecker} from '../../components/passwordchecker/passwordchecker';
import {
  VITE_LINKEDIN_CLIENT_ID,
  VITE_LINKEDIN_REDIRECT_URI,
  VITE_LINKEDIN_SCOPE,
  VITE_LINKEDIN_STATE,
} from '../../config';
import {useDraftCountQuery} from '../../hooks/draft/queries/useDraftCountQuery';
import {useExtensionConnection} from '../../hooks/extension/ExtensionProvider';
import {useUpdateNotificationsSettingsMutation} from '../../hooks/notifications/mutations/useUpdateNotificationsSettingsMutation';
import {useNotificationsSettingsQuery} from '../../hooks/notifications/queries/useNotificationsSettingsQuery';
import {usePreferences} from '../../hooks/preferences/PreferencesProvider';
import {useIsUserLoading, useOrganizations, useUser} from '../../hooks/session/useSessionSelectors';
import {useApiFailureSnackbar} from '../../hooks/useApiFailureSnackbar';
import {HANDLES_API_FAILURE} from '../../utils/mutationMeta';
import {DeleteAccountDialog} from './DeleteAccountDialog';

const logger = new ConsoleLogger('Profile');

type TableCellProps = {
  width?: string;
  children: React.ReactNode;
  align?: string;
  padding: number[];
  noBorder?: boolean;
};

type AppearanceModeOption = 'system' | 'light' | 'dark';

export function TableCell({width, align, padding, children}: TableCellProps) {
  return (
    <Box
      component="th"
      sx={{
        width: width ?? 'auto',
        pt: padding[0],
        pr: padding[1],
        pb: padding[2],
        pl: padding[3],
        textAlign: align ?? 'left',
      }}
    >
      <Typography component="div" variant="body2" color="text">
        {children}
      </Typography>
    </Box>
  );
}

type QueryParams = {
  response_type: string;
  client_id: string;
  redirect_uri: string;
  state: string;
  scope: string;
};

export function Profile() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const mixpanel = useMixpanelInstance();

  const user = useUser();
  const organizations = useOrganizations();
  const loadingUser = useIsUserLoading();
  const {setAppearanceMode: setPreferredAppearanceMode} = usePreferences();

  const [basicInfoForm, setBasicInfoForm] = useState({
    email: user?.email ?? '',
  });
  const [formSubmited, setFormSubmited] = useState(false);
  const [successBasicInfo, setsuccessBasicInfo] = useState(false);
  const [notificationsSettings, setNotificationsSettings] = useState<Omit<EmailSettings, 'user' | '_id'>>({
    refreshTokenExpiration: false,
    extensionPing: false,
    marketingEmails: false,
    successfulPost: false,
    failedPost: false,
    proofreadingPost: false,
  });

  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [removeAccountError, setRemoveAccountError] = useState(false);
  const onShowDeleteAccountDialog = (value: boolean) => {
    setRemoveAccountError(false);
    setShowDeleteAccountDialog(value);
  };
  // Décompte serveur, chargé seulement dialogue ouvert (cf. `useDraftCountQuery`).
  const {data: draftCount} = useDraftCountQuery({enabled: showDeleteAccountDialog});
  const notificationsSettingsQuery = useNotificationsSettingsQuery();
  const updateNotificationsSettings = useUpdateNotificationsSettingsMutation();

  const [changePasswordForm, setChangePasswordForm] = useState<{
    state: 'idle' | 'loading' | 'success' | 'error';
    currentPassword: string;
    newPassword: string;
    newPasswordRepeat: string;
    errorMessageNew: string | undefined;
    errorMessageCurrent: string | undefined;
  }>({
    state: 'idle',
    currentPassword: '',
    newPassword: '',
    newPasswordRepeat: '',
    errorMessageNew: undefined,
    errorMessageCurrent: undefined,
  });
  const refreshMe = useRefreshMe();
  const logout = useLogoutMutation();
  // Refus affiché par `onRemoveAccount` : le 409 dans le dialogue, les autres en snackbar.
  const removeAccount = useRemoveAccountMutation(HANDLES_API_FAILURE);
  const notifyApiFailure = useApiFailureSnackbar();
  const updateProfile = useUpdateProfileMutation();
  // Échec affiché par l'alerte `settings.appearance.error`.
  const updateAppearanceMode = useUpdateAppearanceModeMutation(HANDLES_API_FAILURE);
  const unlinkLinkedin = useUnlinkLinkedinMutation();
  const {reloadSession} = useExtensionConnection();
  const [invalidPassword, setInvalidPassword] = useState(false);
  const [appearanceMode, setAppearanceMode] = useState<AppearanceModeOption>(user?.appearanceMode ?? 'system');
  const [appearanceModeState, setAppearanceModeState] = useState<'idle' | 'success' | 'error'>('idle');
  const [savingAppearanceMode, setSavingAppearanceMode] = useState(false);
  const appearanceTimeoutRef = useRef<number>();

  const clearAppearanceTimeout = () => {
    if (appearanceTimeoutRef.current !== undefined) {
      window.clearTimeout(appearanceTimeoutRef.current);
      appearanceTimeoutRef.current = undefined;
    }
  };

  const onCloseAppearanceAlert = () => {
    clearAppearanceTimeout();
    setAppearanceModeState('idle');
  };

  useEffect(() => {
    setAppearanceMode(user?.appearanceMode ?? 'system');
  }, [user?.appearanceMode]);

  useEffect(() => {
    return () => {
      clearAppearanceTimeout();
    };
  }, []);

  const onSave = async () => {
    setFormSubmited(true);
    setsuccessBasicInfo(false);
    const {email} = basicInfoForm;
    try {
      await updateProfile.mutateAsync({email});
    } catch {
      // Refus affiché par le filet global des mutations : on débloque le formulaire.
      return;
    } finally {
      setFormSubmited(false);
    }
    setsuccessBasicInfo(true);
    setTimeout(() => setsuccessBasicInfo(false), 2000);
  };

  const onConfirmPassword = () => {
    const {currentPassword, newPasswordRepeat, newPassword} = changePasswordForm;
    if (newPassword !== newPasswordRepeat && newPassword.length === 0) {
      return;
    }
    setChangePasswordForm({...changePasswordForm, state: 'loading'});
    updatePassword({oldPassword: currentPassword, newPassword})
      .then(() => {
        setChangePasswordForm({
          state: 'success',
          currentPassword: '',
          newPassword: '',
          newPasswordRepeat: '',
          errorMessageCurrent: undefined,
          errorMessageNew: undefined,
        });
      })
      .catch((err) => {
        logger.warn(err);
        if (err instanceof AuthError) {
          if (err.name === 'InvalidPasswordException') {
            setChangePasswordForm({
              ...changePasswordForm,
              errorMessageCurrent: undefined,
              errorMessageNew: err.message,
              state: 'error',
            });
          } else if (err.name === 'NotAuthorizedException') {
            setChangePasswordForm({
              ...changePasswordForm,
              errorMessageCurrent: err.message,
              errorMessageNew: undefined,
              state: 'error',
            });
          } else {
            Sentry.captureException(err);
            setChangePasswordForm({
              ...changePasswordForm,
              errorMessageCurrent: undefined,
              // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
              errorMessageNew: `Unknown error: ${err}`,
              state: 'error',
            });
          }
        } else {
          Sentry.captureException(err);
          setChangePasswordForm({
            ...changePasswordForm,
            errorMessageCurrent: undefined,
            errorMessageNew: `Unknown error: ${err}`,
            state: 'error',
          });
        }
      });
  };

  // La saisie du mot de confirmation est validée par le dialogue lui-même.
  const onRemoveAccount = async () => {
    setRemoveAccountError(false);
    try {
      await removeAccount.mutateAsync();
      mixpanel.track('account-deleted');
      mixpanel.track('logout', {reason: 'account-deleted'});
      mixpanel.logout();
      logout.mutate();
      navigate('/signin');
    } catch (err) {
      // 409 = seul owner d'une team avec un abonnement en cours → annuler/transférer d'abord.
      const statusCode = (err as {statusCode?: number}).statusCode;
      if (statusCode === 409) {
        setRemoveAccountError(true);
      } else {
        notifyApiFailure(err, {fallback: t('apiFailure.generic')});
      }
    }
  };

  const handleLogout = () => {
    mixpanel.track('logout', {reason: 'settings-profile'});
    mixpanel.logout();
    logout.mutate();
    navigate('/signin');
  };

  const signInWithLinkedIn = () => {
    const getURLWithQueryParams = (base: string, params: QueryParams) => {
      const query = Object.entries(params)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&');
      return `${base}?${query}`;
    };

    const LINKEDIN_URL = getURLWithQueryParams('https://www.linkedin.com/oauth/v2/authorization', {
      response_type: 'code',
      client_id: VITE_LINKEDIN_CLIENT_ID,
      redirect_uri: VITE_LINKEDIN_REDIRECT_URI,
      state: VITE_LINKEDIN_STATE,
      scope: VITE_LINKEDIN_SCOPE,
    });
    window.location.href = LINKEDIN_URL;
  };

  const onUnlinkLinkedin = () => {
    unlinkLinkedin
      .mutateAsync()
      .then(() => {
        mixpanel.track('linkedin-disconnected');
        void reloadSession();
        refreshMe();
      })
      .catch(() => {
        // Refus affiché par le filet global des mutations.
      });
  };

  const onNotificationChange = (key: keyof EmailSettings, value: boolean) => {
    setNotificationsSettings((prev) => ({...prev, [key]: value}));
    updateNotificationsSettings.mutate({
      ...notificationsSettings,
      [key]: value,
    });
  };

  const onAppearanceModeChange = (_event: React.MouseEvent<HTMLElement>, value: AppearanceModeOption | null) => {
    if (value === null || value === appearanceMode) {
      return;
    }

    clearAppearanceTimeout();
    setAppearanceMode(value);
    setAppearanceModeState('idle');
    setSavingAppearanceMode(true);

    // Le mode est posé localement d'abord (affichage immédiat, et conservé au
    // prochain démarrage), puis enregistré côté serveur.
    setPreferredAppearanceMode(value);
    updateAppearanceMode
      .mutateAsync({appearanceMode: value})
      .then(() => {
        setAppearanceModeState('success');
        appearanceTimeoutRef.current = window.setTimeout(() => {
          setAppearanceModeState('idle');
          appearanceTimeoutRef.current = undefined;
        }, 2000);
      })
      .catch((error: unknown) => {
        setAppearanceModeState('error');
        setAppearanceMode(user?.appearanceMode ?? 'system');
        Sentry.captureException(error);
      })
      .finally(() => {
        setSavingAppearanceMode(false);
      });
  };

  useEffect(() => {
    void refreshMe();
  }, []);

  // Les réglages serveur alimentent la copie locale UNE fois ; elle suit ensuite les
  // interrupteurs sans être écrasée par la relecture qui suit chaque enregistrement.
  const notificationsSeeded = useRef(false);
  useEffect(() => {
    if (notificationsSettingsQuery.data !== undefined && !notificationsSeeded.current) {
      notificationsSeeded.current = true;
      setNotificationsSettings(notificationsSettingsQuery.data);
    }
  }, [notificationsSettingsQuery.data]);

  return (
    <>
      <Grid size={{xs: 6}} sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end'}}>
        <Button variant="outlined" color="info" onClick={handleLogout}>
          {t('settings.menu.logoutbtn')}
        </Button>
      </Grid>
      <Grid container spacing={2} size={{xs: 12}} display="flex" alignContent="flex-start" alignItems="center">
        <Grid size={{xs: 12}} display="flex" alignItems="center" gap={2}>
          <Typography sx={{fontWeight: 700, fontSize: '24px'}}>{t('settings.appearance.title')}</Typography>
        </Grid>
        <Grid size={{xs: 12}}>
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  {t('settings.appearance.description')}
                </Typography>
                <ToggleButtonGroup
                  color="primary"
                  exclusive
                  value={appearanceMode}
                  onChange={onAppearanceModeChange}
                  disabled={savingAppearanceMode}
                >
                  <ToggleButton value="system">{t('settings.appearance.system')}</ToggleButton>
                  <ToggleButton value="light">{t('settings.appearance.light')}</ToggleButton>
                  <ToggleButton value="dark">{t('settings.appearance.dark')}</ToggleButton>
                </ToggleButtonGroup>
                {appearanceModeState === 'success' ? (
                  <Alert severity="success" onClose={onCloseAppearanceAlert}>
                    {t('settings.appearance.success')}
                  </Alert>
                ) : null}
                {appearanceModeState === 'error' ? (
                  <Alert severity="error" onClose={onCloseAppearanceAlert}>
                    {t('settings.appearance.error')}
                  </Alert>
                ) : null}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid container spacing={2} size={{xs: 12}} display="flex" alignContent="flex-start" alignItems="center">
        <Grid size={{xs: 6}} display="flex" alignItems="center" gap={2}>
          <Typography sx={{fontWeight: 700, fontSize: '24px'}}>{t('settings.profile.title')}</Typography>
        </Grid>
        <Grid size={{xs: 12}}>
          <Card>
            <CardContent>
              <Box sx={{display: 'flex', flexDirection: 'row', gap: '10px'}}>
                <Avatar src={user?.lnPicture} variant="rounded" />
                <Box sx={{display: 'flex', flexDirection: 'column'}}>
                  <Typography>
                    {user?.firstname} {user?.lastname}
                  </Typography>
                  <Typography>{t('settings.profile.personalaccount')}</Typography>
                </Box>
              </Box>
              {organizations.map((organization) => (
                <React.Fragment key={organization._id}>
                  <Divider sx={{mt: 2, mb: 2}} />
                  <Box sx={{display: 'flex', flexDirection: 'row', gap: '10px'}}>
                    <Avatar src={organization.picture} variant="rounded" />
                    <Box sx={{display: 'flex', flexDirection: 'column'}}>
                      <Typography>{organization.localizedName}</Typography>
                      <Typography>{t('settings.accounts.organizationaccount')}</Typography>
                    </Box>
                  </Box>
                </React.Fragment>
              ))}
            </CardContent>
          </Card>
          <Box sx={{mt: 2, display: 'flex', gap: 2}}>
            <Button
              variant="contained"
              disabled={formSubmited}
              onClick={signInWithLinkedIn}
              startIcon={<LinkedinIcon />}
            >
              {t('settings.linkedaccount.updatewithlinkedinbtn')}
            </Button>
            {user?.vanityName !== undefined && (
              <Button color="error" variant="outlined" disabled={formSubmited} onClick={onUnlinkLinkedin}>
                {t('settings.linkedaccount.unlinklinkedinaccount')}
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={2} size={{xs: 12}} display="flex" alignContent="flex-start" alignItems="center">
        <Grid size={{xs: 12}} display="flex" alignItems="center" gap={2}>
          <Typography sx={{fontWeight: 700, fontSize: '24px'}}>{t('settings.security.title')}</Typography>
        </Grid>
        <Grid size={{xs: 12}}>
          <Card>
            <CardContent>
              <Grid container spacing={3}>
                <Grid
                  size={{xs: 12, sm: 6}}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    alignItems: 'flex-start',
                    gap: '10px',
                  }}
                >
                  <TextField
                    variant="standard"
                    label={t('settings.secuity.email.label')}
                    value={basicInfoForm.email}
                    onChange={(e) => setBasicInfoForm({...basicInfoForm, email: e.target.value})}
                    fullWidth
                  />
                  {successBasicInfo ? (
                    <Alert color="success" onClose={() => setsuccessBasicInfo(false)}>
                      <Typography variant="body2" color="white">
                        {t('settings.confirmsave')}
                      </Typography>
                    </Alert>
                  ) : null}
                  <Button
                    variant="outlined"
                    onClick={onSave}
                    disabled={formSubmited || basicInfoForm.email === user?.email}
                  >
                    {t('settings.secuity.email.savebtn')}
                  </Button>
                </Grid>
                <Grid
                  size={{xs: 12, sm: 6}}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    alignItems: 'flex-start',
                    gap: '10px',
                  }}
                >
                  <TextField
                    variant="standard"
                    label={t('settings.secuity.currentpassword.label')}
                    value={changePasswordForm.currentPassword}
                    error={changePasswordForm.errorMessageCurrent !== undefined}
                    slotProps={{
                      input: {type: 'password'},
                    }}
                    onChange={(e) =>
                      setChangePasswordForm({
                        ...changePasswordForm,
                        currentPassword: e.target.value,
                        errorMessageCurrent: undefined,
                        state: 'idle',
                      })
                    }
                    fullWidth
                  />
                  {changePasswordForm.errorMessageCurrent !== undefined && (
                    <Typography variant="body2" fontWeight="bold" color="error">
                      {changePasswordForm.errorMessageCurrent}
                    </Typography>
                  )}
                  <TextField
                    variant="standard"
                    label={t('settings.secuity.newpassword.label')}
                    value={changePasswordForm.newPassword}
                    error={changePasswordForm.errorMessageNew !== undefined}
                    slotProps={{
                      input: {type: 'password'},
                    }}
                    onChange={(e) =>
                      setChangePasswordForm({
                        ...changePasswordForm,
                        errorMessageNew: '',
                        newPassword: e.target.value,
                        state: 'idle',
                      })
                    }
                    fullWidth
                  />
                  <TextField
                    variant="standard"
                    label={t('settings.secuity.confirmnewpassword.label')}
                    slotProps={{
                      input: {type: 'password'},
                    }}
                    value={changePasswordForm.newPasswordRepeat}
                    onChange={(e) =>
                      setChangePasswordForm({
                        ...changePasswordForm,
                        newPasswordRepeat: e.target.value,
                        state: 'idle',
                      })
                    }
                    fullWidth
                  />
                  <Box
                    sx={{
                      flexDirection: 'row',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      width: '100%',
                    }}
                  >
                    <Box sx={{flexDirection: 'column'}}>
                      <Typography color="secondary" sx={{fontWeight: 700, fontSize: '14px'}}>
                        {t('settings.secuity.passwordrequirement.title')}
                      </Typography>
                      <PasswordChecker
                        password={changePasswordForm.newPassword}
                        repeatPassword={changePasswordForm.newPasswordRepeat}
                        onPasswordInvalid={() => {
                          setInvalidPassword(true);
                        }}
                        onPasswordValid={() => {
                          setInvalidPassword(false);
                        }}
                      />
                    </Box>
                    <Button
                      disabled={
                        changePasswordForm.state === 'success' ||
                        invalidPassword ||
                        changePasswordForm.newPassword !== changePasswordForm.newPasswordRepeat
                      }
                      variant="outlined"
                      onClick={onConfirmPassword}
                    >
                      {changePasswordForm.state === 'loading' ? (
                        <CircularProgress size={20} />
                      ) : changePasswordForm.state === 'success' ? (
                        <CheckIcon />
                      ) : (
                        t('settings.password.savebtn')
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid container spacing={2} size={{xs: 12}} display="flex" alignContent="flex-start" alignItems="center">
        <Grid size={{xs: 12}} display="flex" alignItems="center" gap={2}>
          <Typography sx={{fontWeight: 700, fontSize: '24px'}}>{t('settings.notification.title')}</Typography>
        </Grid>
        <Grid size={{xs: 12}}>
          <Card>
            <CardContent>
              <Table sx={{minWidth: '36rem'}}>
                <Box component="thead">
                  <TableRow>
                    <TableCell width="100%" padding={[1.5, 3, 1.5, 0.5]}>
                      {t('settings.notifications.title')}
                    </TableCell>
                    <TableCell align="center" padding={[1.5, 3, 1.5, 3]}>
                      {t('settings.notifications.email')}
                    </TableCell>
                  </TableRow>
                </Box>
                <TableBody>
                  <TableRow>
                    <TableCell padding={[1, 1, 1, 0.5]}>
                      <Box lineHeight={1.4}>
                        <Typography sx={{fontSize: '14px', lineHeight: '20px'}}>
                          {t('settings.notifications.expiredlinkedin.title')}
                        </Typography>
                        <Typography sx={{fontSize: '12px', lineHeight: '20px', color: 'text.secondary'}}>
                          {t('settings.notifications.expiredlinkedin.description')}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center" padding={[1, 1, 1, 0.5]}>
                      <Switch
                        checked={notificationsSettings.refreshTokenExpiration}
                        onChange={(e, c) => onNotificationChange('refreshTokenExpiration', c)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell padding={[1, 1, 1, 0.5]}>
                      <Box lineHeight={1.4}>
                        <Typography sx={{fontSize: '14px', lineHeight: '20px'}}>
                          {t('settings.notifications.extensionping.title')}
                        </Typography>
                        <Typography sx={{fontSize: '12px', lineHeight: '20px', color: 'text.secondary'}}>
                          {t('settings.notifications.extensionping.description')}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center" padding={[1, 1, 1, 0.5]}>
                      <Switch
                        checked={notificationsSettings.extensionPing}
                        onChange={(e, c) => onNotificationChange('extensionPing', c)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell padding={[1, 1, 1, 0.5]}>
                      <Box lineHeight={1.4}>
                        <Typography sx={{fontSize: '14px', lineHeight: '20px'}}>
                          {t('settings.notifications.marketingemails.title')}
                        </Typography>
                        <Typography sx={{fontSize: '12px', lineHeight: '20px', color: 'text.secondary'}}>
                          {t('settings.notifications.marketingemails.description')}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center" padding={[1, 1, 1, 0.5]}>
                      <Switch
                        checked={notificationsSettings.marketingEmails}
                        onChange={(e, c) => onNotificationChange('marketingEmails', c)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell padding={[1, 1, 1, 0.5]}>
                      <Box lineHeight={1.4}>
                        <Typography sx={{fontSize: '14px', lineHeight: '20px'}}>
                          {t('settings.notifications.successfulPost.title')}
                        </Typography>
                        <Typography sx={{fontSize: '12px', lineHeight: '20px', color: 'text.secondary'}}>
                          {t('settings.notifications.successfulPost.description')}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center" padding={[1, 1, 1, 0.5]}>
                      <Switch
                        checked={notificationsSettings.successfulPost}
                        onChange={(e, c) => onNotificationChange('successfulPost', c)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell padding={[1, 1, 1, 0.5]}>
                      <Box lineHeight={1.4}>
                        <Typography sx={{fontSize: '14px', lineHeight: '20px'}}>
                          {t('settings.notifications.failedPost.title')}
                        </Typography>
                        <Typography sx={{fontSize: '12px', lineHeight: '20px', color: 'text.secondary'}}>
                          {t('settings.notifications.failedPost.description')}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center" padding={[1, 1, 1, 0.5]}>
                      <Switch
                        checked={notificationsSettings.failedPost}
                        onChange={(e, c) => onNotificationChange('failedPost', c)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell padding={[1, 1, 1, 0.5]}>
                      <Box lineHeight={1.4}>
                        <Typography sx={{fontSize: '14px', lineHeight: '20px'}}>
                          {t('settings.notifications.proofreadingPost.title')}
                        </Typography>
                        <Typography sx={{fontSize: '12px', lineHeight: '20px', color: 'text.secondary'}}>
                          {t('settings.notifications.proofreadingPost.description')}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center" padding={[1, 1, 1, 0.5]}>
                      <Switch
                        checked={notificationsSettings.proofreadingPost}
                        onChange={(e, c) => onNotificationChange('proofreadingPost', c)}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid container spacing={2} size={{xs: 12}} display="flex" alignContent="flex-start" alignItems="center">
        <Grid size={{xs: 12}} display="flex" alignItems="center" gap={2}>
          <Typography sx={{fontWeight: 700, fontSize: '24px'}}>{t('settings.removeaccount.title')}</Typography>
        </Grid>
        <Grid size={{xs: 12}}>
          <Card>
            <CardContent>
              <Typography sx={{fontSize: '14px', lineHeight: '29px'}}>
                {t('settings.removeaccount.description')}
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                variant="outlined"
                color="error"
                sx={{height: '100%'}}
                disabled={formSubmited}
                onClick={() => onShowDeleteAccountDialog(true)}
              >
                {t('settings.removeaccount.btn')}
              </Button>
              <DeleteAccountDialog
                open={showDeleteAccountDialog}
                draftCount={draftCount}
                loading={loadingUser === true || removeAccount.isPending}
                subscriptionError={removeAccountError}
                onClose={() => onShowDeleteAccountDialog(false)}
                onConfirm={onRemoveAccount}
              />
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}
